import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";

import FontStylesheet from "components/website/components/FontStylesheet";
import StipplLogo from "components/website/img/stippl-logo.svg";
import { sizedPhoto, srcSetFor } from "utils/photoUrl";
import {
	creatorAppLink,
	CreatorListCard,
	CreatorMap,
	CreatorNextTrip,
	CreatorPageData,
	CreatorTripCard,
	curvePath,
	initialsOf,
	isRepeat,
	plural,
	splitTagline,
} from "../creatorPage";
import { MARKER_GLYPHS, S_MARK_PATH, UI_ICONS, UiIconName } from "./creatorPageGlyphs";
import styles from "./CreatorPage.module.scss";

/**
 * `/c/<username>`: a creator's page. A port of the approved template (stippl-knowledge
 * `work/creator-page/profile.template.html`, spec `work/creator-page.md`).
 *
 * It renders EXACTLY what the server loader returned (`loadCreatorPage`, via `initialData`) and nothing else, and
 * every link in it is already a finished href with the creator's tracking on it. So the server HTML and the
 * hydrating render agree: what is decided after mount is only what is inherently the client's (the photo slideshow,
 * the tab that follows the scroll, the share and copy actions, and the hover maps' photos, which are fetched when
 * a card is first hovered or focused so a phone never pays for them).
 *
 * Standalone, like /links: its own slim header, no marketing navigation or footer. English only.
 */

interface CreatorPageProps {
	/** Server-side pre-fetched data (`loadCreatorPage`, creatorPageLoader.ts), forwarded by `MarketingShell`. */
	initialData?: CreatorPageData | null;
}

const cx = (...names: (string | false | null | undefined)[]): string => names.filter(Boolean).join(" ");

// Google Fonts loads the handwriting face (signature, polaroid caption); Poppins and Rubik are already on every marketing page.
const HANDWRITING_FONT = "https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap";

// The S the hero photos show through, tilted as in the template.
const S_MASK = `url("data:image/svg+xml,${encodeURIComponent(
	`<svg xmlns="http://www.w3.org/2000/svg" viewBox="-3.3 -3.9 87 116"><path fill="black" transform="rotate(-14 40.5 54)" d="${S_MARK_PATH}"/></svg>`
)}")`;

const SOCIAL_ICON: Record<string, UiIconName> = { instagram: "instagram", tiktok: "tiktok", youtube: "video", link: "link" };
const SOCIAL_COLOR: Record<string, string> = { instagram: "#c13584", tiktok: "#111111" };

// Slot widths, in CSS px, for photos we can ask a CDN to resize (Unsplash, Pexels, Wikimedia): the large card, a small one, a list card.
const BIG_CARD_WIDTH = 760;
const CARD_WIDTH = 500;
const LIST_CARD_WIDTH = 560;
const SLIDE_MS = 4800;
const TOAST_MS = 2200;

const Icon = ({ name, color }: { name: UiIconName; color: string }) => (
	<svg viewBox="0 0 32 32" aria-hidden="true">
		<path d={UI_ICONS[name]} fill={color} />
	</svg>
);

/** The green hand-drawn underline under the last words of a heading. */
const Squiggle = () => (
	<svg viewBox="0 0 300 20" preserveAspectRatio="none" aria-hidden="true">
		<path d="M3 14 C 70 4, 160 3, 297 10" fill="none" stroke="#13c892" strokeWidth="6" strokeLinecap="round" />
	</svg>
);

const placeStyle = (point: { x: number; y: number }): CSSProperties => ({ left: `${point.x}%`, top: `${point.y}%` });

/**
 * A Mapbox image with things positioned on it in percent. The stage is sized as `object-fit: cover` would size the
 * image inside its container (see the SCSS), so the percentages stay on the map whatever the container's shape.
 */
const MapStage = ({ map, showImage, children }: { map: { image: string; width: number; height: number }; showImage: boolean; children: ReactNode }) => (
	<span className={styles.mapStage} style={{ "--ar": map.width / map.height } as CSSProperties}>
		{showImage && <img src={map.image} alt="" />}
		{children}
	</span>
);

/** The route line through a map's stops, and a dot on each (a stop that repeats an earlier one has no second dot). */
const RouteOverlay = ({ map, bends, small, nowIndex }: { map: CreatorMap; bends: number[]; small?: boolean; nowIndex?: number | null }) => (
	<>
		<svg className={styles.route} viewBox={`0 0 ${map.width} ${map.height}`} preserveAspectRatio="none" aria-hidden="true">
			<path
				d={curvePath(map.points, map.width, map.height, bends)}
				fill="none"
				stroke="#EA5681"
				strokeWidth="3"
				strokeLinecap="round"
				vectorEffect="non-scaling-stroke"
			/>
		</svg>
		{map.points.map((point, index) =>
			isRepeat(map.points, index) ? null : (
				<span key={index} className={cx(styles.mapDot, small && styles.mapDotSm, index === nowIndex && styles.mapDotNow)} style={placeStyle(point)} />
			)
		)}
	</>
);

/** Starts true once the card has been hovered or focused: the map photo is only requested from then on. */
const useArmed = () => {
	const [armed, setArmed] = useState(false);
	return { armed, arm: () => setArmed(true) };
};

/** A cover that moves on to the next photo when one fails to load, and is left out when none does. */
const FallbackPhoto = ({ urls, width, className }: { urls: string[]; width: number; className?: string }) => {
	const [index, setIndex] = useState(0);
	const ref = useRef<HTMLImageElement>(null);
	// A photo that failed before hydration fired its error before React was listening.
	useEffect(() => {
		if (ref.current?.complete && ref.current.naturalWidth === 0) setIndex((i) => i + 1);
	}, [index]);
	const url = urls[index];
	if (!url) return null;
	return (
		<img
			ref={ref}
			className={className}
			src={sizedPhoto(url, width)}
			srcSet={srcSetFor(url, width) || undefined}
			alt=""
			loading="lazy"
			decoding="async"
			onError={() => setIndex((i) => i + 1)}
		/>
	);
};

interface TripCardProps {
	trip: CreatorTripCard;
	big: boolean;
	onShare: (url: string) => void;
}

const TripCard = ({ trip, big, onShare }: TripCardProps) => {
	const { armed, arm } = useArmed();
	const width = big ? BIG_CARD_WIDTH : CARD_WIDTH;
	return (
		<div className={cx(styles.trip, big && styles.tripBig)} id={`creator-trip-${trip.id}`} onMouseEnter={arm} onFocus={arm}>
			{trip.cover && <FallbackPhoto key={trip.cover} urls={[trip.cover, ...(trip.coverFallbacks ?? [])]} width={width} className={styles.tripCover} />}
			{trip.map && (
				<span className={styles.hmap} aria-hidden="true">
					<MapStage map={trip.map} showImage={armed}>
						<RouteOverlay map={trip.map} bends={[0.14, -0.14]} />
					</MapStage>
				</span>
			)}
			{trip.flag && (
				<span className={styles.tripFlag} aria-hidden="true">
					{trip.flag}
				</span>
			)}
			{trip.status === "upcoming" && <span className={cx(styles.chip, styles.chipSoon)}>Coming up</span>}
			{trip.status === "now" && <span className={cx(styles.chip, styles.chipLive)}>Live now</span>}
			<button
				className={styles.share}
				id={`creator-trip-share-${trip.id}`}
				type="button"
				aria-label={`Copy the link to ${trip.title}`}
				onClick={() => onShare(trip.shareUrl)}
			>
				<Icon name="share" color="#0f3e4a" />
			</button>
			<span className={styles.tripOverlay}>
				<a className={styles.tripTitle} href={trip.href}>
					{trip.title}
				</a>
				<span className={styles.tripMeta}>
					<span>
						{trip.days} {plural(trip.days, "day")}
					</span>
					<span>
						{trip.stops} {plural(trip.stops, "stop")}
					</span>
				</span>
			</span>
		</div>
	);
};

const ListCard = ({ list }: { list: CreatorListCard }) => {
	const { armed, arm } = useArmed();
	return (
		<a className={styles.list} id={`creator-list-${list.id}`} href={list.href} onMouseEnter={arm} onFocus={arm}>
			<span className={styles.listPhoto}>
				{list.cover && <FallbackPhoto key={list.cover} urls={[list.cover, ...(list.coverFallbacks ?? [])]} width={LIST_CARD_WIDTH} />}
				{list.map && (
					<span className={styles.hmap} aria-hidden="true">
						<MapStage map={list.map} showImage={armed}>
							{list.map.pins.map((pin, index) => (
								<span key={index} className={styles.pin} style={{ ...placeStyle(pin), background: pin.tint }}>
									<svg viewBox="0 0 32 32" aria-hidden="true" dangerouslySetInnerHTML={{ __html: MARKER_GLYPHS[pin.glyph] ?? MARKER_GLYPHS.star }} />
								</span>
							))}
						</MapStage>
					</span>
				)}
				<span className={styles.listOverlay}>
					<b>{list.title}</b>
					<small>
						{list.placeCount} {plural(list.placeCount, "place")}
					</small>
				</span>
			</span>
			<span className={styles.listBody}>
				{list.description && <span className={styles.listDescription}>{list.description}</span>}
				<span className={styles.save}>Save to my Stippl</span>
			</span>
		</a>
	);
};

/** "Next trip" or "Travelling now", with the trip, over the hero's bottom edge. */
const NextTripBar = ({ trip }: { trip: CreatorNextTrip }) => {
	const next = trip.mode === "next";
	return (
		<div id="creator-next-trip" className={cx(styles.live, !trip.map && styles.liveNoMap)}>
			{trip.map && (
				<div className={styles.liveMap}>
					<MapStage map={trip.map} showImage>
						<RouteOverlay map={trip.map} bends={[0.2]} small nowIndex={trip.map.currentIndex} />
					</MapStage>
				</div>
			)}
			<div className={styles.liveText}>
				<span className={cx(styles.badge, next && styles.badgeNext)}>
					<i />
					{next ? "Next trip" : "Travelling now"}
				</span>
				<b>{[trip.flag, trip.title].filter(Boolean).join(" ")}</b>
				<small>
					{next
						? `Leaves ${trip.startLabel} · ${trip.days} ${plural(trip.days, "day")} · ${trip.stops} ${plural(trip.stops, "stop")}`
						: `Day ${trip.day} of ${trip.days}${trip.currentStop ? ` · now in ${trip.currentStop}` : ""}`}
				</small>
				{!next && (
					<span className={styles.progress}>
						<span style={{ width: `${Math.min(100, Math.round(((trip.day ?? 0) / trip.days) * 100))}%` }} />
					</span>
				)}
			</div>
			<div className={styles.liveActions}>
				<a className={cx(styles.btn, styles.btnGreen)} id="creator-make-it-yours" href={trip.makeItYoursHref}>
					Make it yours
				</a>
				<a className={cx(styles.btn, styles.btnGhost)} id="creator-follow-trip" href={trip.followHref}>
					Follow
				</a>
			</div>
		</div>
	);
};

type TabId = "trips" | "lists";

const CreatorPage = ({ initialData }: CreatorPageProps = {}) => {
	// Nothing to show without the loader's data, and no way to fetch it here: the page has no GraphQL.
	const data = initialData ?? null;

	const [slide, setSlide] = useState(0);
	const [activeTab, setActiveTab] = useState<TabId>("trips");
	const [toast, setToast] = useState({ text: "", on: false });
	const toastTimer = useRef<ReturnType<typeof setTimeout>>();

	const photoCount = data?.heroPhotos.length ?? 0;
	const hasBothSections = Boolean(data && data.trips.length > 0 && data.lists.length > 0);
	const firstTab: TabId = data && data.trips.length > 0 ? "trips" : "lists";

	// The hero photos take turns in the S, unless the visitor asked for less motion.
	useEffect(() => {
		if (photoCount < 2 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
		const timer = setInterval(() => setSlide((current) => (current + 1) % photoCount), SLIDE_MS);
		return () => clearInterval(timer);
	}, [photoCount]);

	// The tab of the section in view lights up.
	useEffect(() => {
		if (!hasBothSections) return undefined;
		const onScroll = () => {
			const listsTop = document.getElementById("creator-lists")?.getBoundingClientRect().top;
			setActiveTab(listsTop !== undefined && listsTop < 160 ? "lists" : "trips");
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, [hasBothSections]);

	useEffect(() => () => clearTimeout(toastTimer.current), []);

	if (!data) return null;

	const showToast = (text: string) => {
		setToast({ text, on: true });
		clearTimeout(toastTimer.current);
		toastTimer.current = setTimeout(() => setToast((current) => ({ ...current, on: false })), TOAST_MS);
	};

	const copyLink = async (url: string) => {
		try {
			if (!navigator.clipboard) throw new Error("no clipboard");
			await navigator.clipboard.writeText(url);
			showToast("Share link copied");
		} catch {
			// Blocked (an insecure context, an in-app browser, a denied permission): show the link so it can be copied by hand.
			showToast(url);
		}
	};

	const shareProfile = async () => {
		if (navigator.share) {
			try {
				await navigator.share({ title: `${data.displayName} on Stippl`, url: data.pageUrl });
				return;
			} catch (error: any) {
				// Closing the share sheet is an answer, not a failure.
				if (error?.name === "AbortError") return;
			}
		}
		await copyLink(data.pageUrl);
	};

	const { lead, accent: tagAccent } = splitTagline(data.tagline);
	const place = data.hometown ? [data.hometown.flag, `Based in ${data.hometown.name}`].filter(Boolean).join(" ") : "";
	const nextTrip = data.nextTrip;
	const tabs = [
		{ id: "trips" as const, label: "Trips", count: data.trips.length },
		{ id: "lists" as const, label: "Lists", count: data.lists.length },
	].filter((tab) => tab.count > 0);
	const avatar = data.avatar ? <img src={data.avatar} alt="" /> : initialsOf(data.displayName);

	return (
		<div id="stippl-creator-page" className={styles.root} style={{ "--cr": data.accent } as CSSProperties}>
			<FontStylesheet href={HANDWRITING_FONT} />

			<header className={styles.nav}>
				<div className={cx(styles.wrap, styles.navInner)}>
					<a className={styles.logo} id="creator-logo" href={data.homeHref}>
						<img src={StipplLogo} alt="Stippl" width={91} height={24} />
					</a>
					<a className={cx(styles.btn, styles.btnGreen)} id="creator-get-app" href={creatorAppLink(data.username, "nav")}>
						Get the app
					</a>
				</div>
			</header>

			<section className={styles.hero} aria-labelledby="creator-h1">
				<div className={styles.heroInner}>
					<div className={styles.copy}>
						<span className={styles.pill}>
							<b>Stippl creator</b>
							{place}
						</span>
						<div className={styles.nameRow}>
							<div className={styles.avatar}>{avatar}</div>
							<h1 id="creator-h1">
								{data.displayName}
								<small className={styles.handle}>@{data.username}</small>
							</h1>
						</div>
						{data.tagline && (
							<p className={styles.tagline}>
								{lead && `${lead} `}
								<span className={styles.tagAccent}>
									{tagAccent}
									<Squiggle />
								</span>
							</p>
						)}
						{data.hello && <p className={styles.hello}>{data.hello}</p>}
						{data.intro && <p className={styles.bio}>{data.intro}</p>}
						{data.signature && <p className={styles.signature}>{data.signature}</p>}
						{data.facts.length > 0 && (
							<div className={styles.facts}>
								{data.facts.map((fact, index) => (
									<span key={index}>{[fact.emoji, fact.text].filter(Boolean).join(" ")}</span>
								))}
							</div>
						)}
						<div className={styles.stats}>
							<div>
								<b>{data.counts.trips}</b>
								<small>{plural(data.counts.trips, "trip")}</small>
							</div>
							<div>
								<b>{data.counts.countries}</b>
								<small>{plural(data.counts.countries, "country", "countries")}</small>
							</div>
							{data.followers && (
								<div>
									<b>{data.followers}</b>
									<small>followers</small>
								</div>
							)}
						</div>
						{data.socials.length > 0 && (
							<div className={styles.socials}>
								{data.socials.map((social, index) => (
									<a key={index} href={social.url} rel="noopener">
										<Icon name={SOCIAL_ICON[social.type] ?? "link"} color={SOCIAL_COLOR[social.type] ?? "#0f3e4a"} />
										{social.label}
									</a>
								))}
							</div>
						)}
						<div className={styles.actions}>
							<a className={cx(styles.btn, styles.btnGreen)} id="creator-follow" href={data.followHref}>
								<Icon name="plus" color="#fff" />
								Follow on Stippl
							</a>
							<button className={cx(styles.btn, styles.btnGhost)} id="creator-share" type="button" onClick={shareProfile}>
								<Icon name="share" color="#0f3e4a" />
								Share profile
							</button>
						</div>
					</div>

					<div className={styles.art} aria-hidden="true" style={{ "--smask": S_MASK } as CSSProperties}>
						{data.heroPhotos.length > 0 && (
							<div className={styles.slides}>
								<div className={styles.slidesInner}>
									{data.heroPhotos.map((photo, index) => (
										<img key={index} src={photo} alt="" className={index === slide ? styles.slideOn : undefined} />
									))}
								</div>
							</div>
						)}
						{nextTrip && (
							<div className={styles.chipCard}>
								<span className={cx(styles.chipDot, nextTrip.mode === "next" && styles.chipDotNext)} />
								<span>
									<b>{[nextTrip.flag, nextTrip.mode === "next" ? "Next trip" : "Travelling now"].filter(Boolean).join(" ")}</b>
									{nextTrip.title} · {nextTrip.mode === "next" ? `leaves ${nextTrip.startLabel}` : `day ${nextTrip.day}`}
								</span>
							</div>
						)}
						{data.polaroid && (
							<figure className={styles.polaroid}>
								<img src={data.polaroid.image} alt="" />
								<figcaption>{data.polaroid.caption}</figcaption>
							</figure>
						)}
						<span className={cx(styles.tile, styles.tileA)}>
							<Icon name="instagram" color="#ea5681" />
						</span>
						<span className={cx(styles.tile, styles.tileB)}>
							<Icon name="check" color="#0e9e73" />
						</span>
					</div>
				</div>
			</section>

			<main className={cx(styles.wrap, styles.wide)}>
				{nextTrip && <NextTripBar trip={nextTrip} />}

				{tabs.length > 0 && (
					<nav className={styles.tabs} id="creator-tabs" aria-label="Sections">
						{tabs.map((tab) => (
							<a
								key={tab.id}
								id={`creator-tab-${tab.id}`}
								href={`#creator-${tab.id}`}
								className={tab.id === (hasBothSections ? activeTab : firstTab) ? styles.tabOn : undefined}
							>
								{tab.label}
								<small>{tab.count}</small>
							</a>
						))}
					</nav>
				)}

				{data.trips.length > 0 && (
					<section id="creator-trips" className={styles.section} aria-labelledby="creator-trips-h">
						<div className={styles.sectionHead}>
							<span className={styles.eyebrow}>My trips</span>
							<h2 id="creator-trips-h">
								Where I&apos;ve{" "}
								<span className={styles.underlined}>
									been
									<Squiggle />
								</span>
							</h2>
							<p>Every route I planned in Stippl. Open one and make it yours.</p>
						</div>
						<div className={styles.trips}>
							{data.trips.map((trip, index) => (
								<TripCard key={trip.id} trip={trip} big={index === 0} onShare={copyLink} />
							))}
						</div>
					</section>
				)}

				{data.lists.length > 0 && (
					<section id="creator-lists" className={styles.section} aria-labelledby="creator-lists-h">
						<div className={styles.sectionHead}>
							<span className={styles.eyebrow}>My lists</span>
							<h2 id="creator-lists-h">
								Places I keep{" "}
								<span className={styles.underlined}>
									going back to
									<Squiggle />
								</span>
							</h2>
							<p>Save one to your Stippl and add them to any trip.</p>
						</div>
						<div className={cx(styles.lists, data.lists.length % 3 === 0 && data.lists.length % 4 !== 0 && styles.listsThree)}>
							{data.lists.map((list) => (
								<ListCard key={list.id} list={list} />
							))}
						</div>
					</section>
				)}
			</main>

			<section className={styles.closer} aria-label="Follow">
				<div className={cx(styles.wrap, styles.wide, styles.closerInner)}>
					<div className={styles.avatar}>{avatar}</div>
					<div>
						<h2>Plan your next trip with me</h2>
						<p>Follow {data.displayName} on Stippl and every new trip and list lands in your app.</p>
					</div>
					<div className={styles.actions}>
						<a className={cx(styles.btn, styles.btnGreen)} id="creator-closer-follow" href={data.followHref}>
							<Icon name="plus" color="#fff" />
							Follow on Stippl
						</a>
						<button className={cx(styles.btn, styles.btnGhost)} id="creator-closer-share" type="button" onClick={shareProfile}>
							<Icon name="share" color="#0f3e4a" />
							Share
						</button>
					</div>
				</div>
			</section>

			<footer className={styles.footer}>
				<div className={cx(styles.wrap, styles.wide, styles.footerInner)}>
					<span>Made with Stippl · Plan, travel, relive</span>
					<a id="creator-become" href={data.creatorsHref}>
						Become a Stippl creator
					</a>
					{data.credits.length > 0 && (
						<details className={styles.credits}>
							<summary>Photo credits</summary>
							<p>{data.credits.join(" · ")}</p>
						</details>
					)}
				</div>
			</footer>

			<div className={cx(styles.toast, toast.on && styles.toastOn)} role="status">
				{toast.text}
			</div>
		</div>
	);
};

export default CreatorPage;
